import { Tokens } from '@/assets/tokens'
import { Button } from '@/components'
import { useRouting } from '@/hooks'
import { Alert, Api, Confirmation } from '@/modules'
import { DeleteFive, FolderOpen } from '@icon-park/react'
import { useEffect, useState } from 'react'
import { BaseModal } from '../Modal/Modal.component'
import {
  ActionButton,
  ButtonContainer,
  ConfigDate,
  ConfigDetails,
  ConfigItem,
  ConfigListContainer,
  ConfigName,
  EmptyMessage,
  LoadingMessage,
  ConfigPreview,
  ConfigPreviewTitle,
  ConfigPreviewContent,
  ConfigPreviewTag,
  ConfigActions,
} from './FilterModal.styles'
import { getFilterDetails } from './FilterDetailsHelper'

interface FilterConfigItem {
  id: string
  name: string
  type: string
  created: string
  updated: string
  parameters: Record<string, any>
}

interface LoadFilterModalProps {
  isOpen: boolean
  onClose: () => void
  onLoad: (config: Record<string, any>) => void
  filterType: 'MENTION' | 'DASHBOARD'
}

export function LoadFilterModal({ isOpen, onClose, onLoad, filterType }: LoadFilterModalProps) {
  const [configs, setConfigs] = useState<FilterConfigItem[]>([])
  const [selectedConfig, setSelectedConfig] = useState<FilterConfigItem | null>(null)
  const { companyID } = useRouting()
  const { sendAlert } = Alert.Hook()

  const [loadConfigs, isLoadingConfigs, configsData] = Api.LazyQuery<{ elements: FilterConfigItem[] }>(
    `companies/${companyID}/filter-configs?type=${filterType}`,
  )

  const { run: deleteConfig } = Api.Command(Api.Methods.DELETE, `companies/${companyID}/filter-configs/:configID`)

  const { open: openDeleteConfirmation } = Confirmation.Hook({
    icon: DeleteFive,
    text: 'Esta ação não pode ser desfeita. Tem certeza que deseja excluir esta configuração?',
    confirmText: 'Sim! Excluir configuração',
  })

  useEffect(() => {
    if (companyID && isOpen) {
      loadConfigs()
    }
  }, [companyID, isOpen])

  useEffect(() => {
    if (configsData?.elements) {
      setConfigs(configsData.elements)
    }
  }, [configsData])

  const handleLoad = () => {
    if (!selectedConfig) {
      sendAlert({
        type: 'error',
        title: 'Nenhuma configuração selecionada',
        message: 'Por favor, selecione uma configuração para carregar.',
      })
      return
    }

    onLoad(selectedConfig.parameters)
    onClose()
  }

  const handleDelete = (config: FilterConfigItem) => {
    openDeleteConfirmation(() => {
      deleteConfig({
        binds: [['configID', config.id]],
        onSuccess: () => {
          sendAlert({
            type: 'success',
            title: 'Configuração excluída',
            message: `A configuração "${config.name}" foi excluída com sucesso.`,
          })
          setConfigs((prev) => prev.filter((c) => c.id !== config.id))
          if (selectedConfig?.id === config.id) {
            setSelectedConfig(null)
          }
        },
        onFail: (error) => {
          sendAlert({
            type: 'error',
            title: 'Erro ao excluir',
            message: `Não foi possível excluir a configuração: ${error.message}`,
          })
        },
      })
    })
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title="Carregar Configuração de Filtro" maxWidth="40rem">
      <ConfigListContainer>
        {isLoadingConfigs ?
          <LoadingMessage>Carregando configurações...</LoadingMessage>
        : configs.length === 0 ?
          <EmptyMessage>Nenhuma configuração salva encontrada.</EmptyMessage>
        : configs.map((config) => {
            const configDetails = getFilterDetails(config.parameters, filterType)
            return (
              <ConfigItem
                key={config.id}
                $selected={selectedConfig?.id === config.id}
                onClick={() => setSelectedConfig(config)}>
                <ConfigDetails>
                  <ConfigName>{config.name}</ConfigName>
                  <ConfigDate>Atualizada em: {formatDate(config.updated)}</ConfigDate>
                  {configDetails.length > 0 && (
                    <ConfigPreview>
                      <ConfigPreviewTitle>Filtros aplicados:</ConfigPreviewTitle>
                      <ConfigPreviewContent>
                        {configDetails.slice(0, 6).map((detail, index) => (
                          <ConfigPreviewTag key={index}>{detail}</ConfigPreviewTag>
                        ))}
                        {configDetails.length > 6 && (
                          <ConfigPreviewTag>+{configDetails.length - 6} mais</ConfigPreviewTag>
                        )}
                      </ConfigPreviewContent>
                    </ConfigPreview>
                  )}
                </ConfigDetails>
                <ConfigActions>
                  <Button.Root
                    $variant="danger"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation()
                      handleDelete(config)
                    }}
                    style={{
                      minWidth: 'auto',
                      width: '32px',
                      height: '32px',
                      padding: 0,
                    }}
                    title="Excluir configuração">
                    <Button.Icon value={DeleteFive} color={Tokens.ColorWhite} />
                  </Button.Root>
                </ConfigActions>
              </ConfigItem>
            )
          })
        }
      </ConfigListContainer>

      <ButtonContainer>
        <ActionButton>
          <Button.Root $variant="secondary" onClick={onClose}>
            <Button.Text value="Cancelar" />
          </Button.Root>
        </ActionButton>
        <ActionButton>
          <Button.Root $variant="primary" onClick={handleLoad} disabled={!selectedConfig}>
            <Button.Icon value={FolderOpen} color={Tokens.ColorWhite} />
            <Button.Text value="Carregar" />
          </Button.Root>
        </ActionButton>
      </ButtonContainer>
    </BaseModal>
  )
}
